<script setup lang="ts">
import { useAuthStore } from "~/store/auth/auth.store";
import { AuthModeEnum } from "~/utils/enums/auth.enums";
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
const authStore = useAuthStore();
const banner = ref<HTMLElement | null>(null);
const leaving = ref(false);
const accountIdentity = computed(() => {
  const user = authStore.user;
  const name = [user?.firstname, user?.lastname].filter(Boolean).join(" ");
  return name && user?.email ? `${name} (${user.email})` : name || user?.email || "cet utilisateur";
});

// The banner can wrap on narrower screens; reserve its actual height in the page layout.
watch(
  banner,
  (element, _, onCleanup) => {
    if (!element) return;
    const updateHeight = () => {
      document.documentElement.style.setProperty(
        "--impersonation-banner-height",
        `${element.getBoundingClientRect().height}px`,
      );
    };
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    updateHeight();
    onCleanup(() => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--impersonation-banner-height");
    });
  },
  { flush: "post" },
);

async function leave() {
  if (leaving.value) return;
  leaving.value = true;
  try {
    await authStore.exitImpersonation();
  } finally {
    leaving.value = false;
  }
}
</script>

<template>
  <div
    v-if="authStore.mode === AuthModeEnum.IMPERSONATION && !authStore.impersonation?.administratorIsSimbelAdmin"
    ref="banner"
    role="status"
    class="sticky top-0 z-[100] flex flex-col gap-2 bg-light-orange-color px-4 py-2 text-secondary-color sm:flex-row sm:items-center sm:justify-between sm:gap-6"
  >
    <div class="flex min-w-0 items-center gap-3">
      <span class="h-4 w-4 shrink-0 text-primary-orange-color" aria-hidden="true">
        <SvgIcon name="warning_error" class="h-full w-full" />
      </span>
      <p class="body_demi min-w-0 break-words">Vous êtes connecté avec le compte de {{ accountIdentity }}.</p>
    </div>
    <AppButton
      label="Revenir à votre compte"
      type="link"
      class="ml-7 shrink-0 self-start !text-secondary-color hover:!text-primary-color disabled:!text-medium-grey-color sm:ml-0 sm:self-center [&>span]:!text-xs [&>span]:!font-semibold"
      :disabled="leaving"
      @click="leave"
    />
  </div>
</template>
